import DualDateRangePicker from 'components/Common/Calendar/DualDateRangePicker'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { addDays, addMonths, beginningOfToday } from 'lib/datetime/dateUtils'
import type { Moment } from 'moment'
import { useCallback, useContext, useMemo } from 'react'
import ExperiencesSearchDateQuickChips, {
  CHIP_ANYTIME,
  type ExperiencesDateQuickChipSelectPayload,
} from './ExperiencesSearchDateQuickChips'
import styled from 'styled-components'

const CalendarWrapper = styled.div`
  width: 100%;

  .vertical {
    flex-direction: column;

    > * {
      flex-grow: 1;

      & + * {
        margin-left: 0;
      }
    }
  }
`

interface Props {
  calendarDirection?: 'vertical' | 'horizontal';
  quickChipsDisplayType?: 'single-line' | 'multi-line';
}

function ExperiencesSearchDateSelectInline(props: Props) {
  const { calendarDirection = 'horizontal', quickChipsDisplayType } = props

  const { checkinDate, checkoutDate, dateSearchOptionId } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const today = useMemo(() => beginningOfToday(), [])

  const maxDate = useMemo(
    () =>
      checkinDate && !checkoutDate ?
          addDays(checkinDate.toDate(), 31) :
          addMonths(today, 36),
    [checkinDate, checkoutDate, today],
  )

  const unsetDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
  }, [globalSearchDispatch])

  const unsetFlexibleDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })
  }, [globalSearchDispatch])

  const selectSpecificDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.SPECIFIC })
    unsetFlexibleDates()
  }, [globalSearchDispatch, unsetFlexibleDates])

  const handleDatesChange = useCallback(
    (dates: { startDate: Moment, endDate: Moment | undefined }) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKIN_DATE,
        date: dates.startDate,
      })
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKOUT_DATE,
        date: dates.endDate,
      })
      if (dates.startDate) {
        selectSpecificDates()
      }
    },
    [globalSearchDispatch, selectSpecificDates],
  )

  const selectAnytime = useCallback(() => {
    unsetDates()
    unsetFlexibleDates()
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.ANYTIME })
    globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: true })
  }, [globalSearchDispatch, unsetDates, unsetFlexibleDates])

  const selectDayRange = useCallback((start: Moment, end: Moment) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: start })
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: end })
    selectSpecificDates()
  }, [globalSearchDispatch, selectSpecificDates])

  const onQuickChipSelect = useCallback(
    (payload: ExperiencesDateQuickChipSelectPayload) => {
      if (payload.value === CHIP_ANYTIME || !payload.range) {
        selectAnytime()
        return
      }
      selectDayRange(payload.range.start, payload.range.end)
    },
    [selectAnytime, selectDayRange],
  )

  return (
    <>
      <ExperiencesSearchDateQuickChips
        checkinDate={checkinDate}
        checkoutDate={checkoutDate}
        dateSearchOptionId={dateSearchOptionId}
        onSelect={onQuickChipSelect}
        displayType={quickChipsDisplayType}
      />
      <CalendarWrapper>
        <DualDateRangePicker
          onDatesChange={handleDatesChange}
          startDate={checkinDate}
          endDate={checkoutDate}
          minDate={today}
          maxDate={maxDate}
          allowSameDayRange
          className={calendarDirection}
        />
      </CalendarWrapper>
    </>
  )
}

export default ExperiencesSearchDateSelectInline
